import { map, tap, filter } from 'rxjs';
import { isEqual, isMatch } from 'lodash-es';
import { RowManager } from '../../../../utils/row-manager';
import schema from '../../../../schema.json';

export class AlarmRowManager extends RowManager {
    storeConfig(config) {
        super.storeConfig(config);

        this.alarmName = config.alarmName;
        this.keyTemplate = config.keyTemplate;

        this.usedIndices = [];
        this.removedKeys = [];
    }

    findBestLastIndex(numbers) {
        /*
         * We have to find an index to continue generating alarm indices
         * at that is least likely having the code reuse indices.
         * If the alarm keys are 1, 3, 5, then this would return 6.
         * If the they are 97, 99, 3, then it would return 4.
         */
        const sorted = numbers.sort((a, b) => a - b);
        let maxGap = -1;

        for (let i = 0; i < sorted.length; i++) {
            const current = sorted[i];
            const next = sorted[(i + 1) % sorted.length];

            // JavaScript's module can return negative values,
            // so `+ maxKeys` to make sure it is always positive.
            const gap = (next - current + schema.maxAlarmKeys) % schema.maxAlarmKeys;

            if (gap > maxGap) {
                maxGap = gap;
                this.lastIndex = current;
            }
        }
    }

    // Method to handle incoming rows.
    handleInputRows(input$) {
        // Each key is something like `max_alarm_1`, we want to get the `1` for each.
        const regex = new RegExp(this.keyTemplate.replace('{index}', '(\\d+)'));

        return super.handleInputRows(
            input$.pipe(
                // Find the best index to continue at from the `max_alarm_1` keys.
                tap((input) => {
                    // Converts the `max_alarm_1` into a number 1.
                    const numbers = (input[this.alarmName] ?? []).flatMap((row) => {
                        const match = row.key.match(regex);
                        return match ? [parseInt(match[1], 10)] : [];
                    });

                    // Only call findBestLastIndex once on the first time numbers contains data.
                    if (!this.lastIndex && numbers.length) {
                        this.findBestLastIndex(numbers);

                        // Keep numbers around to keep track of what has already been used.
                        this.usedIndices = numbers;
                    }
                }),
                // Extract the alarm rows from the notification config.
                // E.g. pulls the `maxAlarm` array from the notification config object.
                map((input) => input[this.alarmName] ?? [])
            )
        );
    }

    // Method to handle outgoing rows.
    handleOutputRows(rows$) {
        return super.handleOutputRows(rows$).pipe(
            // Grab the latest 'input' which is the latest version of the notification config.
            // Merge the modified alarm rows back into the notification config.
            // Remove any enabledAlarms for which the alarm rows do not exist anymore.
            map((rows) => {
                const input = this.input$.getValue();

                let { recipients } = input;

                recipients = input.recipients.map((recipient) => ({
                    ...recipient,
                    enabledAlarms: recipient.enabledAlarms.filter(
                        (key) => !this.removedKeys.includes(key)
                    ),
                }));

                const output = {
                    ...input,
                    [this.alarmName]: rows,
                    recipients,
                };

                return [output, input];
            }),
            // If the merged result is the same as the latest notification config, then we do
            // nothing.
            filter(([output, input]) => !isEqual(output, input)),
            // If it is different, then output the new notification config.
            map(([output, _input]) => output)
        );
    }

    isEmptyRow(row) {
        return isMatch(row, this.emptyRow);
    }

    interpolate(template, values) {
        return template.replace(/{(\w+)}/g, (match, key) => (key in values ? values[key] : match));
    }

    createKey(index) {
        return this.interpolate(this.keyTemplate, { index });
    }

    removeRow(index) {
        // Get the get belonging to this row.
        const { key } = this.rows$.getValue()[index];

        // Have the key excluded upon handleOutputRows.
        this.removedKeys.push(key);

        // Now remove the actual row.
        super.removeRow(index);
    }

    createRow(_rows) {
        // In case createRow gets called before handleInputRows.
        const lastIndex = this.lastIndex || 0;

        let index = lastIndex;

        // Generate a key and check if it already exists in the rows.
        do {
            // Move the key index and make sure it wraps around.
            index = (index + 1) % schema.maxAlarmKeys;

            // In case we make it back to the lastIndex, we were unable to find an empty key and
            // this would block forever.
            if (index === lastIndex) throw new Error('Out of keys.');

            // Keep searching as long as the index was already used before.
        } while (this.usedIndices.includes(index));

        // Mark this index used.
        this.usedIndices.push(index);

        // Create a key from the discovered index.
        const key = this.createKey(index);

        return { ...this.emptyRow, key };
    }
}
